import { useCallback, useMemo, useState } from "react"
import { useQueryWithEndpoint, useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  GetAlertTemplatesForSystemDocument,
  EnableAlertRuleDocument,
  DisableAlertRuleDocument,
  UpdateAlertThresholdDocument,
  ResetAlertToDefaultsDocument,
} from "@hydrologiq/gql-experience/graphql"
import type { AlertAssignment, AlertRuleState, AlertTemplate } from "@hydrologiq/shared/components/alerts"
import { buildAlertRules } from "@hydrologiq/shared/components/alerts/alertRules"

// --- Types ---

export type { AlertTemplate, AlertAssignment, AlertRuleState } from "@hydrologiq/shared/components/alerts"

export interface SystemAsset {
  id: string
  name: string
  class?: string | null
  model?: string | null
  /** A Module's parent */
  parentId?: string | null
}

export interface AssetAlertGroup {
  asset: SystemAsset
  /** The Asset's Modules, whose alerts are listed with its own */
  modules: SystemAsset[]
  rules: AlertRuleState[]
  activeCount: number
  inactiveCount: number
  totalCount: number
}

export interface UseAlertConfigurationReturn {
  assetGroups: AssetAlertGroup[]
  systemRules: AlertRuleState[]
  loading: boolean
  error: string | undefined
  enableRule: (templateId: string, targetId: string, targetType: "asset" | "system") => Promise<void>
  disableRule: (assignmentId: string) => Promise<void>
  updateThreshold: (
    assignmentId: string | undefined | null,
    thresholdOverride: number,
    templateId?: string,
    targetId?: string,
    targetType?: "asset" | "system",
  ) => Promise<void>
  updateTimeWindow: (
    assignmentId: string | undefined | null,
    timeWindowOverrideSeconds: number,
    templateId?: string,
    targetId?: string,
    targetType?: "asset" | "system",
  ) => Promise<void>
  resetToDefaults: (assignmentId: string) => Promise<void>
  enabling: boolean
  disabling: boolean
  updating: boolean
  resetting: boolean
}

export function useAlertConfiguration(systemId: string): UseAlertConfigurationReturn {
  const [enabling, setEnabling] = useState(false)
  const [disabling, setDisabling] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [resetting, setResetting] = useState(false)

  // Fetch templates and assignments for all scopes
  const { data, loading, error, refetch } = useQueryWithEndpoint(
    GetAlertTemplatesForSystemDocument,
    {
      variables: { systemId, scope: "" },
      skip: !systemId,
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  const [enableAlertRuleMutation] = useMutationWithEndpoint(
    EnableAlertRuleDocument,
    undefined,
    "primary",
  )

  const [disableAlertRuleMutation] = useMutationWithEndpoint(
    DisableAlertRuleDocument,
    undefined,
    "primary",
  )

  const [updateAlertThresholdMutation] = useMutationWithEndpoint(
    UpdateAlertThresholdDocument,
    undefined,
    "primary",
  )

  const [resetAlertToDefaultsMutation] = useMutationWithEndpoint(
    ResetAlertToDefaultsDocument,
    undefined,
    "primary",
  )

  // Merge templates + assignments into UI state
  const { assetGroups, systemRules } = useMemo(() => {
    const templates = (data?.alertTemplatesForSystem?.templates ?? []) as AlertTemplate[]
    const assignments = (data?.alertTemplatesForSystem?.assignments ?? []) as AlertAssignment[]
    const assets: SystemAsset[] = data?.alertTemplatesForSystem?.assets ?? []
    const modules: SystemAsset[] = data?.alertTemplatesForSystem?.modules ?? []

    const isCustom = (t: AlertTemplate) => t.origin?.toUpperCase() === "CUSTOM"
    const assetTemplates = templates.filter((t) => t.scopeLevel === "asset" || isCustom(t))

    // An Asset's alerts and its Modules', counted as the Asset's own page counts them
    const assetGroups: AssetAlertGroup[] = assets.map((asset) => {
      const own = modules.filter((m) => m.parentId === asset.id)
      const rules = buildAlertRules(assetTemplates, assignments, [asset, ...own])
      const activeCount = rules.filter((r) => r.isEnabled).length
      return {
        asset,
        modules: own,
        rules,
        activeCount,
        inactiveCount: rules.length - activeCount,
        totalCount: rules.length,
      }
    })

    // System-level rules: Standard Alerts only
    const systemTemplates = templates.filter((t) => t.scopeLevel === "system" && !isCustom(t))
    const systemRules = buildAlertRules(systemTemplates, assignments, [{ id: systemId }])

    return { assetGroups, systemRules }
  }, [data, systemId])

  const enableRule = useCallback(
    async (templateId: string, targetId: string, targetType: "asset" | "system") => {
      setEnabling(true)
      try {
        const input: Record<string, string> = { templateId }
        if (targetType === "asset") {
          input.assetId = targetId
        } else {
          input.systemId = targetId
        }
        const { errors } = await enableAlertRuleMutation({ variables: { input } })
        if (errors?.length) throw new Error(errors[0].message)
        await refetch()
      } finally {
        setEnabling(false)
      }
    },
    [enableAlertRuleMutation, refetch],
  )

  const disableRule = useCallback(
    async (assignmentId: string) => {
      setDisabling(true)
      try {
        const { errors } = await disableAlertRuleMutation({ variables: { assignmentId } })
        if (errors?.length) throw new Error(errors[0].message)
        await refetch()
      } finally {
        setDisabling(false)
      }
    },
    [disableAlertRuleMutation, refetch],
  )

  const updateThreshold = useCallback(
    async (
      assignmentId: string | undefined | null,
      thresholdOverride: number,
      templateId?: string,
      targetId?: string,
      targetType?: "asset" | "system",
    ) => {
      setUpdating(true)
      try {
        if (assignmentId) {
          const input = { assignmentId, thresholdOverride }
          const { errors } = await updateAlertThresholdMutation({ variables: { input } })
          if (errors?.length) throw new Error(errors[0].message)
        } else if (templateId && targetId) {
          const input: Record<string, any> = {
            templateId,
            thresholdOverride,
            enabled: false,
          }
          if (targetType === "asset") {
            input.assetId = targetId
          } else {
            input.systemId = targetId
          }
          const { errors } = await enableAlertRuleMutation({ variables: { input } })
          if (errors?.length) throw new Error(errors[0].message)
        }
        await refetch()
      } finally {
        setUpdating(false)
      }
    },
    [enableAlertRuleMutation, updateAlertThresholdMutation, refetch],
  )

  const updateTimeWindow = useCallback(
    async (
      assignmentId: string | undefined | null,
      timeWindowOverrideSeconds: number,
      templateId?: string,
      targetId?: string,
      targetType?: "asset" | "system",
    ) => {
      setUpdating(true)
      try {
        if (assignmentId) {
          const input = { assignmentId, timeWindowOverrideSeconds }
          const { errors } = await updateAlertThresholdMutation({ variables: { input } })
          if (errors?.length) throw new Error(errors[0].message)
        } else if (templateId && targetId) {
          const input: Record<string, any> = {
            templateId,
            timeWindowOverrideSeconds,
            enabled: false,
          }
          if (targetType === "asset") {
            input.assetId = targetId
          } else {
            input.systemId = targetId
          }
          const { errors } = await enableAlertRuleMutation({ variables: { input } })
          if (errors?.length) throw new Error(errors[0].message)
        }
        await refetch()
      } finally {
        setUpdating(false)
      }
    },
    [enableAlertRuleMutation, updateAlertThresholdMutation, refetch],
  )

  const resetToDefaults = useCallback(
    async (assignmentId: string) => {
      setResetting(true)
      try {
        const { errors } = await resetAlertToDefaultsMutation({ variables: { assignmentId } })
        if (errors?.length) throw new Error(errors[0].message)
        await refetch()
      } finally {
        setResetting(false)
      }
    },
    [resetAlertToDefaultsMutation, refetch],
  )

  return {
    assetGroups,
    systemRules,
    loading,
    error: error?.message,
    enableRule,
    disableRule,
    updateThreshold,
    updateTimeWindow,
    resetToDefaults,
    enabling,
    disabling,
    updating,
    resetting,
  }
}
